'use client';

import React, { useMemo, useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { ErrorBoundary } from 'react-error-boundary';

import { builder, BuilderComponent } from '@builder.io/react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  useBuilderPostHogIntegration,
  extractBuilderABTestData,
} from './useBuilderPostHogIntegration';
import { BuilderABTestProvider } from './BuilderABTestContext';
import {
  usePostHogBuilderVariant,
  trackPostHogBuilderExperiment,
} from './usePostHogBuilderVariant';
import { useGetCustomFingerprint } from 'hooks/useGetCustomFingerprint';
import { trackSEEvent, trackEvent } from 'utils/analytics';
import { safePostHogCapture } from 'utils/posthogHelpers';
import {
  mapRegulationTypeToCanonical,
  inferRegulationTypeFromSlug,
  resolveCurrentAmountRaised,
  resolveCurrentInvestorCount,
} from 'utils/offerings/helpers';
import {
  applyPreviewStatusOverride,
  getPreviewOverrideFromLocation,
} from 'utils/offerings/previewStatusOverride';
import { getAmountRaised } from 'services/offerings';
import { EDITORIAL_TOKEN_VARS } from './designTokens';
import EditorialOfferingHero from './editorial/EditorialOfferingHero';
import CampaignScripts from '../CampaignScripts';
import PerIssuerExperimentExposure from '../PerIssuerExperimentExposure';
import ClosedOfferingPopup from '../PrivateOfferingPopup/ClosedOfferingPopup';
import OfferingUpdateHandler from '../Updates/OfferingUpdateHandler';
import TestBanner from '../Banners/TestBanner';
import { normalizePageType } from 'utils/builderTargeting';
import EvergreenLinks from 'components/Offering/EvergreenLinks';
import { useCampaignAppMode } from './CampaignAppModeContext';
import { CAMPAIGN_APP_ANALYTICS_PROPERTIES } from 'utils/campaignAppBridge';

// Initialize Builder.io at module level
const BUILDER_API_KEY = process.env.NEXT_PUBLIC_BUILDER_API_KEY || '';
const BUILDER_MODEL = process.env.NEXT_PUBLIC_BUILDER_MODEL || 'campaign-page';

// Initialize builder on both server and client
if (BUILDER_API_KEY) {
  builder.init(BUILDER_API_KEY);
}

export const hasLocationAnchor = (location) =>
  Boolean(location?.hash || location?.href?.includes('#'));

// A reload or history restore (back/forward) reuses the session-history entry
// and its saved scroll position. In an in-app WebView (e.g. Meta ads opening
// the campaign page in the Facebook browser) an involuntary reload is common,
// and the browser's native scroll restoration already returns the reader to
// where they were — so we must NOT force those loads back to the top. Only a
// genuine fresh navigation gets the reset.
export const isRestorableNavigation = () => {
  if (typeof performance === 'undefined') return false;
  const [navigationEntry] =
    typeof performance.getEntriesByType === 'function'
      ? performance.getEntriesByType('navigation')
      : [];
  if (navigationEntry?.type) {
    return navigationEntry.type === 'reload' || navigationEntry.type === 'back_forward';
  }
  const legacyType = performance.navigation?.type;
  return legacyType === 1 || legacyType === 2;
};

// Register custom components with Builder.io
import './register-components';

// Error fallback for Builder content
const BuilderErrorFallback = ({ error, resetErrorBoundary }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#FCF9F5',
      color: '#111827',
      p: 4,
    }}
  >
    <Typography variant="h4" sx={{ mb: 2, color: '#ef4444' }}>
      Error Loading Builder Content
    </Typography>
    <Typography sx={{ color: '#6B7280', textAlign: 'center', maxWidth: 500 }}>
      {error?.message || 'An unexpected error occurred loading the page content.'}
    </Typography>
    <Box
      component="button"
      onClick={resetErrorBoundary}
      sx={{
        mt: 3,
        px: 4,
        py: 1.5,
        backgroundColor: '#185C65',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: 1,
        cursor: 'pointer',
        fontWeight: 600,
      }}
    >
      Try Again
    </Box>
  </Box>
);

// Loading placeholder that matches SSR output to prevent hydration mismatch
const LoadingPlaceholder = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#FCF9F5',
      color: '#111827',
      p: 4,
    }}
  >
    <CircularProgress sx={{ color: '#185C65', mb: 2 }} />
    <Typography sx={{ color: '#6B7280' }}>Loading content...</Typography>
  </Box>
);

/**
 * Inner component that uses the PostHog integration hook
 * Must be inside the component tree to use hooks properly
 */
const BuilderContentWithTracking = ({
  initialContent,
  builderDataContext,
  slug,
  variantKey,
  variantSource,
}) => {
  const trackedDepthsRef = useRef(new Set());
  const trackedSectionsRef = useRef(new Set());

  const isEditing = builder?.isEditing || builder?.isPreviewing;

  const { contentId, variationId, variationName, isInABTest, trackScrollDepth, trackSectionView } =
    useBuilderPostHogIntegration(initialContent, {
      trackImpressionOnMount: !isEditing,
      offeringSlug: slug,
      additionalData: {
        company_name: builderDataContext.companyName,
        posthog_variant_key: variantKey,
        posthog_variant_source: variantSource,
      },
    });

  useEffect(() => {
    if (isEditing) return;

    const DEPTH_THRESHOLDS = [25, 50, 75, 100];

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      const depth = Math.round((window.scrollY / scrollHeight) * 100);

      for (const threshold of DEPTH_THRESHOLDS) {
        if (depth >= threshold && !trackedDepthsRef.current.has(threshold)) {
          trackedDepthsRef.current.add(threshold);
          trackScrollDepth(threshold);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isEditing, trackScrollDepth]);

  useEffect(() => {
    if (isEditing) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const sectionName = entry.target.getAttribute('data-cy');
            if (sectionName && !trackedSectionsRef.current.has(sectionName)) {
              trackedSectionsRef.current.add(sectionName);
              trackSectionView(sectionName);
            }
          }
        }
      },
      { threshold: 0.3 },
    );

    const sections = document.querySelectorAll('[data-cy$="-section"]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [isEditing, trackSectionView]);

  useEffect(() => {
    if (isEditing) return;
    if (variantKey && variantSource === 'query_param') {
      trackPostHogBuilderExperiment(slug, variantKey, {
        content_id: contentId,
        builder_variation_id: variationId,
      });
    }
  }, [slug, variantKey, variantSource, contentId, variationId, isEditing]);

  return (
    <BuilderABTestProvider
      contentId={contentId}
      variationId={variationId}
      variationName={variationName || variantKey}
      offeringSlug={slug}
    >
      <BuilderComponent
        model={BUILDER_MODEL}
        content={initialContent}
        data={{
          ...builderDataContext,
          abTest: {
            contentId,
            variationId,
            variationName: variationName || variantKey,
            isInABTest: isInABTest || !!variantKey,
            posthogVariantKey: variantKey,
            posthogVariantSource: variantSource,
          },
        }}
        options={{ includeRefs: true }}
      />
    </BuilderABTestProvider>
  );
};

BuilderContentWithTracking.propTypes = {
  initialContent: PropTypes.object.isRequired,
  builderDataContext: PropTypes.object.isRequired,
  slug: PropTypes.string,
  variantKey: PropTypes.string,
  variantSource: PropTypes.string,
};

/**
 * BuilderLayoutClient - Client component that renders pre-fetched Builder.io content
 *
 * This component receives content that was fetched server-side, avoiding
 * client-side fetch issues caused by hydration problems.
 *
 * Key features:
 * - Uses isMounted state to defer rendering until after hydration
 * - Integrates with PostHog for A/B test tracking (PostHog-controlled variants)
 * - Tracks content impressions and conversions
 * - Provides A/B test context to child components
 * - Supports PostHog feature flag controlled variant assignment
 *
 * Offering layout selection is handled server-side in page.jsx. SE3 is the
 * default; by the time this component renders, the decision to use Builder
 * has already been made.
 */
const BuilderLayoutClient = ({
  offeringData,
  initialContent,
  variantKey,
  variantSource,
  requestedVariant,
  preview = false,
  previewPageType = null,
  perIssuerFlagKey = null,
}) => {
  const appMode = useCampaignAppMode();
  const slug = offeringData?.slug;
  const user = useSelector((state) => state.user);
  const queryClient = useQueryClient();
  useGetCustomFingerprint();
  const [viewedCampaignPage, setViewedCampaignPage] = useState(false);
  const hasTrackedGTMPageView = useRef(false);
  const sentEnrichedPageviewRef = useRef(false);

  const { variant: posthogVariant, isLoading: variantLoading } = usePostHogBuilderVariant(slug, {
    currentVariant: variantKey,
    variantSource,
    enableRedirect: !requestedVariant,
  });

  const effectiveVariantKey = requestedVariant || posthogVariant || variantKey;

  const isEditorialReskin = effectiveVariantKey === 'se3';
  const isControlVariant = !effectiveVariantKey || effectiveVariantKey === 'control';
  // The CF preview override intentionally makes Builder components behave as
  // post-launch (`approved`) so their CTA/disclaimer can be reviewed before
  // launch. The global LayoutWrapper consumes that same overridden cache and
  // therefore drops the real TTW reservation banner. Keep the control preview
  // faithful to the live control chrome by rendering the banner from the
  // original server offering status while leaving Builder content in CF mode.
  const shouldRenderPreviewTestBanner =
    preview &&
    normalizePageType(previewPageType) === 'CF' &&
    offeringData?.status === 'testing' &&
    isControlVariant;
  const editorialTokenStyle = isEditorialReskin ? EDITORIAL_TOKEN_VARS : undefined;

  const { data: amountRaisedData } = useQuery({
    queryKey: ['amountRaised', offeringData?.id],
    queryFn: () => getAmountRaised({ offeringId: offeringData?.id }),
    enabled: Boolean(isEditorialReskin && offeringData?.id),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const currentAmountRaised = resolveCurrentAmountRaised({
    amountRaisedObject: amountRaisedData,
    offeringData,
  });
  const currentInvestorCount = resolveCurrentInvestorCount({
    amountRaisedObject: amountRaisedData,
    offeringData,
  });

  useEffect(() => {
    if (offeringData && slug) {
      // PD-35758: seed the cache with the preview-overridden status so the
      // CF preview renders as live CF from first paint (no TTW→CF flash) for
      // every `['offeringData']` consumer. No-op outside the CF preview URL.
      const seedData = applyPreviewStatusOverride(offeringData, getPreviewOverrideFromLocation());
      queryClient.setQueryData(['offering', slug], seedData);
      queryClient.setQueryData(['offeringData', slug], seedData);
      queryClient.setQueryData(['offeringData'], seedData);
    }
  }, [offeringData, slug, queryClient]);

  const isBuilderEditing = builder.isEditing || builder.isPreviewing;

  useLayoutEffect(() => {
    if (!isEditorialReskin || isBuilderEditing || typeof window === 'undefined') {
      return undefined;
    }
    if (hasLocationAnchor(window.location)) return undefined;
    if (isRestorableNavigation()) return undefined;

    let userInteracted = false;
    const previousScrollRestoration =
      'scrollRestoration' in window.history ? window.history.scrollRestoration : null;

    const markUserInteracted = () => {
      userInteracted = true;
    };

    const resetScrollTop = () => {
      if (userInteracted || window.scrollY === 0) return;
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    resetScrollTop();
    const frameId = window.requestAnimationFrame(resetScrollTop);
    const interactionEvents = ['touchstart', 'wheel', 'keydown', 'mousedown'];
    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, markUserInteracted, { once: true, passive: true });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markUserInteracted);
      });
      if (previousScrollRestoration) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, [isEditorialReskin, isBuilderEditing]);

  useEffect(() => {
    if (isBuilderEditing) return;
    if (offeringData && !viewedCampaignPage) {
      const appAnalyticsProperties = appMode ? CAMPAIGN_APP_ANALYTICS_PROPERTIES : undefined;
      if (appAnalyticsProperties) {
        trackSEEvent('view_campaign_page', offeringData?.slug, user, appAnalyticsProperties);
      } else {
        trackSEEvent('view_campaign_page', offeringData?.slug, user);
      }
      trackEvent('view_campaign_page', {
        slug: offeringData?.slug,
        name: offeringData?.name,
        regulationType: offeringData?.regulationType,
        status: offeringData?.status,
        ...(appAnalyticsProperties || {}),
      });
      setViewedCampaignPage(true);
    }
  }, [offeringData, user, viewedCampaignPage, isBuilderEditing, appMode]);

  useEffect(() => {
    if (isBuilderEditing) return;
    if (sentEnrichedPageviewRef.current) return;
    if (!offeringData?.slug) return;
    if (variantLoading) return;

    // PD-35415 follow-up — slug-pattern fallback covers QP / Reg D
    // capital-raise pages where `offeringData.regulationType` is null
    // from the offerings API at $pageview time.
    const regulationType =
      mapRegulationTypeToCanonical(offeringData?.regulationType) ??
      inferRegulationTypeFromSlug(offeringData?.slug);
    const enrichedProperties = {
      offering_slug: offeringData.slug,
      regulation_type: regulationType,
      offering_status: offeringData?.status,
      page_layout: 'builder',
      builder_variant_key: effectiveVariantKey || null,
      ...(appMode ? CAMPAIGN_APP_ANALYTICS_PROPERTIES : {}),
    };
    safePostHogCapture('$pageview', enrichedProperties);
    safePostHogCapture('campaign_pageview', enrichedProperties);
    sentEnrichedPageviewRef.current = true;
  }, [offeringData, effectiveVariantKey, variantLoading, isBuilderEditing, appMode]);

  useEffect(() => {
    if (isBuilderEditing) return;
    if (hasTrackedGTMPageView.current) return;
    if (offeringData && typeof window !== 'undefined' && window.dataLayer) {
      const pageType =
        offeringData.status === 'test' || offeringData.status === 'testing'
          ? 'reserve page'
          : 'invest page';

      window.dataLayer.push({
        event: 'issuerPageView',
        id: offeringData?.slug,
        name: offeringData?.name,
        category: offeringData?.regulationType,
        customer_id: user?.primaryUserId,
        pageType,
        page_layout: 'builder',
        builder_variant_key: variantKey || null,
        ...(appMode ? CAMPAIGN_APP_ANALYTICS_PROPERTIES : {}),
        ecommerce: {
          items: [
            {
              item_id: offeringData?.slug,
              item_name: offeringData?.name,
              item_category: offeringData?.regulationType,
              price: offeringData?.pricePerShare
                ? parseFloat(offeringData.pricePerShare)
                : undefined,
              quantity: 1,
              currency: 'USD',
            },
          ],
        },
      });
      hasTrackedGTMPageView.current = true;
    }
  }, [offeringData, user?.primaryUserId, variantKey, isBuilderEditing, appMode]);

  const builderDataContext = useMemo(
    () => ({
      offeringData,
      slug,
      investUrl: `/invest/${slug}`,
      amountRaised: currentAmountRaised ?? 0,
      investorCount: currentInvestorCount ?? 0,
      minInvestment: offeringData?.minInvestment || 100,
      pricePerShare: offeringData?.pricePerShare || 0,
      companyName: offeringData?.name || 'Company',
    }),
    [offeringData, slug, currentAmountRaised, currentInvestorCount],
  );

  const abTestData = useMemo(() => extractBuilderABTestData(initialContent), [initialContent]);

  const handleBuilderError = useCallback(
    (error) => {
      safePostHogCapture('builder_content_error', {
        error_message: error?.message,
        offering_slug: slug,
        builder_content_id: abTestData?.contentId,
        builder_variation_id: abTestData?.testVariationId,
        variant_key: effectiveVariantKey,
      });
    },
    [slug, abTestData, effectiveVariantKey],
  );

  // If in Builder.io editor with no content, render empty BuilderComponent to allow editing
  if (!initialContent && isBuilderEditing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#FCF9F5',
        }}
        data-cy="builder-layout-editing"
      >
        <BuilderComponent
          model={BUILDER_MODEL}
          data={builderDataContext}
          options={{ includeRefs: true, enrich: true }}
        />
      </Box>
    );
  }

  // Preview mode (?preview=true) bypasses the legacy-layout fallback so
  // designers can see Builder edits even when nothing is published yet.
  // When the server fetch returned nothing AND we're not inside the
  // Builder editor iframe, surface a diagnostic instead of an indefinite
  // spinner — otherwise the page looks identical to "still loading" and
  // wastes an hour of triage every time someone fat-fingers a slug or
  // variantKey in Builder. See: tapville-cf5 incident 2026-05-19.
  if (preview && !initialContent && !isBuilderEditing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FCF9F5',
          color: '#111827',
          p: 4,
          textAlign: 'center',
        }}
        data-cy="builder-layout-preview-empty"
      >
        <Typography variant="h5" sx={{ mb: 2, color: '#185C65' }}>
          No Builder.io campaign-page entry matched slug{' '}
          <code style={{ background: '#EDE9E3', padding: '2px 6px', borderRadius: 4 }}>{slug}</code>
        </Typography>
        <Typography sx={{ color: '#6B7280', maxWidth: 560, mb: 2 }}>
          The preview URL only renders a Builder.io entry whose <code>data.slug</code> equals{' '}
          <code>{slug}</code> (or whose URL targeting is <code>/offering/{slug}</code>), and whose{' '}
          <code>data.variantKey</code> is empty or <code>control</code>.
        </Typography>
        <Typography sx={{ color: '#6B7280', maxWidth: 560 }}>
          Open the entry in Builder and double-check those three fields — leading slashes, stray
          spaces, and quoted strings in <strong>Variant Key</strong> are the usual culprits.
        </Typography>
        <Typography sx={{ color: '#6B7280', maxWidth: 560, mt: 2 }}>
          This preview is querying the <code>{BUILDER_MODEL}</code> Builder model. If your entry is
          published in a different model (e.g. <code>campaign-page-stage</code>), it will not match
          — set <code>NEXT_PUBLIC_BUILDER_MODEL</code> for this environment to the model the entry
          lives in.
        </Typography>
      </Box>
    );
  }

  if (variantLoading && !requestedVariant && !initialContent) {
    return <LoadingPlaceholder />;
  }

  return (
    <ErrorBoundary FallbackComponent={BuilderErrorFallback} onError={handleBuilderError}>
      {editorialTokenStyle ? (
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Serif:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap"
        />
      ) : null}
      <PerIssuerExperimentExposure flagKey={perIssuerFlagKey} />
      <CampaignScripts offeringData={offeringData} />
      {shouldRenderPreviewTestBanner && <TestBanner name={offeringData.name} />}
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#FCF9F5',
          overflowX: 'clip',
          maxWidth: '100vw',
          boxSizing: 'border-box',
          position: 'relative',
          scrollPaddingTop: '80px',
          '& sup': {
            fontSize: '0.6em',
            lineHeight: 0,
            position: 'relative',
            verticalAlign: 'baseline',
            top: '-0.5em',
          },
          '& .MuiSkeleton-root': {
            animation: 'none',
            opacity: 0.3,
          },
          '& *': {
            boxSizing: 'border-box',
          },
          '& img, & canvas': {
            maxWidth: '100%',
            height: 'auto',
          },
          '& svg': {
            maxWidth: '100%',
          },
          // Builder page-level blocks can carry authoring-canvas top padding.
          // When the first block contains the campaign hero, that padding
          // renders as an empty strip above the hero in the live/control page.
          // Scope the reset to that exact root shape so body-section spacing
          // and pages whose first block is not a hero remain unchanged.
          '& .builder-content > div > .builder-blocks > .builder-block:first-of-type:has([data-cy="builder-hero-section"])':
            {
              paddingTop: '0 !important',
            },
          '@media (max-width: 899px)': {
            '& div[style], & section[style], & span[style]': {
              maxWidth: '100% !important',
            },
            // Headings, paragraphs, list items: prefer normal-word
            // breaks at whitespace, fall back to mid-word breaks for
            // genuinely unbreakable strings.
            //
            // History (Greenfield Robotics campaign, Jun 2026):
            //   1. Original code: `word-break: break-word` (legacy alias
            //      of `overflow-wrap: anywhere`). Android Chrome split
            //      words at random characters mid-letter even when the
            //      line had room — "Autonomo / us." regression.
            //   2. First fix: `word-break: normal` +
            //      `overflow-wrap: break-word`. Stopped the random
            //      mid-word splits, BUT `break-word` doesn't contribute
            //      to min-content intrinsic sizing — so a long display
            //      title forced the parent grid track wider than the
            //      viewport, which was then clipped by the parent's
            //      `overflow: hidden` and the user lost the right edge
            //      of the headline entirely. Worse than the original.
            //   3. This fix: `word-break: normal` +
            //      `overflow-wrap: anywhere`. Same render behavior as
            //      `break-word` (whitespace breaks preferred, mid-word
            //      as last resort) BUT `anywhere` contributes 0 to
            //      min-content sizing, so flex/grid parents can shrink
            //      to viewport and the last-resort break actually fires.
            //      Combined with `min-width: 0` on the hero's
            //      LeftContent / RightContent grid items.
            '& p, & li, & h1, & h2, & h3, & h4, & h5, & h6, & blockquote': {
              overflowWrap: 'anywhere',
              wordBreak: 'normal',
              hyphens: 'manual',
            },
            '& span': {
              overflowWrap: 'anywhere',
            },
            '& a': {
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            },
            '& table': {
              tableLayout: 'fixed',
              width: '100% !important',
              maxWidth: '100% !important',
            },
            '& [class*="builder-"]:not(.builder-top-nav), & [data-builder-component]:not(.builder-top-nav)':
              {
                maxWidth: '100%',
                overflowX: 'hidden',
              },
          },
          '&[data-builder-editorial-reskin="true"]': {
            // se3 renders the bespoke <EditorialOfferingHero /> above the
            // Builder content, so suppress ONLY the Builder-authored default
            // hero block. `[data-cy="builder-hero-section"]` is the hero's
            // stable root selector and is distinct from every body section
            // (terms/perks/rti/rewards/about/etc.), so body content is
            // untouched. The optional-chaining `:has` is not used — a plain
            // descendant selector keeps the body sections visible.
            '& [data-cy="builder-hero-section"]': {
              display: 'none !important',
            },
            // Unify the page on the editorial cream. Builder section wrappers
            // carry an author-set near-white background (#FDFDFC) that seams
            // against the cream hero/nav; make them transparent so the cream
            // page (builder-layout) shows through uniformly. The editorial
            // cards use borders on cream (not white fills), so this matches the
            // handoff.
            '& .builder-block': {
              backgroundColor: 'transparent !important',
            },
            // Paige QA (2026-07-09): the Terms block carries a Builder-authored
            // left margin (~114px on CancerVAX) that tabs it inward from the
            // content frame while sibling sections (About, etc.) sit flush.
            // Zero the horizontal margins on the .builder-block wrapping the
            // terms section so it aligns with the rest of the page.
            '& .builder-block:has(> [data-cy="builder-classic-terms-section"])': {
              marginLeft: '0 !important',
              marginRight: '0 !important',
            },
            '& h1, & h2, & h3': {
              fontFamily: "'IBM Plex Serif', Georgia, serif !important",
              fontWeight: 500,
              letterSpacing: '-0.01em',
            },
            '& h1': {
              fontWeight: 400,
              letterSpacing: '-0.012em',
            },
            '& h1 em, & h1 i, & h2 em, & h2 i, & h3 em, & h3 i': {
              fontStyle: 'italic',
              color: 'var(--bld-primary, #0C6F64)',
            },
            '& h4, & h5, & h6': {
              fontFamily: "'IBM Plex Serif', Georgia, serif !important",
            },
            [[
              '& [data-cy="builder-classic-story-section"] a[href]',
              '& [data-cy="builder-reference-list"] a[href]',
              '& [data-cy="terms-sec-links"] a[href]',
              '& [data-cy="terms-risks"] a[href]',
              '& [data-cy="terms-perks-info"] a[href]',
              '& [data-cy="editorial-terms"] a[href]',
              '& [data-cy="builder-updates-section"] a[href]',
            ].join(', ')]: {
              color: 'var(--bld-primary, #0C6F64) !important',
              textDecorationColor: 'currentColor !important',
            },
          },
        }}
        style={editorialTokenStyle}
        data-cy="builder-layout"
        data-builder-editorial-reskin={editorialTokenStyle ? 'true' : undefined}
        data-builder-content-id={abTestData?.contentId}
        data-builder-variation-id={abTestData?.testVariationId}
        data-builder-variant-key={effectiveVariantKey}
        data-builder-variant-source={variantSource}
        data-builder-ab-test={abTestData?.isInABTest || !!effectiveVariantKey ? 'true' : 'false'}
        data-builder-preview={preview ? 'true' : 'false'}
        suppressHydrationWarning
      >
        <Box
          sx={{
            minHeight: '100vh',
            // PD-36055 (Hannah): the editorial section nav is a persistent
            // fixed top bar (see BuilderClassicSectionNav), so reserve its height
            // here to keep it from overlapping the hero on load.
            ...(isEditorialReskin
              ? {
                  paddingTop: { xs: '64px', md: '72px' },
                }
              : {}),
          }}
        >
          {isEditorialReskin && (
            <ErrorBoundary FallbackComponent={() => null} onError={handleBuilderError}>
              <EditorialOfferingHero
                offeringData={offeringData}
                amountRaisedData={amountRaisedData}
              />
            </ErrorBoundary>
          )}
          <BuilderContentWithTracking
            initialContent={initialContent}
            builderDataContext={builderDataContext}
            slug={slug}
            variantKey={effectiveVariantKey}
            variantSource={variantSource}
          />
        </Box>
      </Box>
      {/*
        Mirror the legacy v2 Offering layout (see `components/Offering/Body.jsx`,
        `<OfferingUpdateHandler />`) so the campaign-update email links
        (`/offering/<slug>/update/<id>`) trigger the update modal on Builder v3
        pages too. Without this, clicking "Read the full update" from an email
        on a Builder-enabled offering (e.g. aquipor) lands on the campaign page
        but never opens the modal. PD-34801, surfaced 2026-05-19.
      */}
      {/*
        PD-36808: mirror the legacy v2 Offering layout (see
        `components/Offering/Body.jsx`) by rendering the evergreen internal
        links below the Builder campaign content. Organic visitors landing on
        a Builder-rendered offering page (e.g. /offering/liquidpiston) otherwise
        have no onward internal path once they finish the campaign, producing an
        "organic dead end". These links add a relevant next action without
        touching any regulated offering content. Hidden inside the Builder
        editor so authors don't see a non-Builder block in the visual editor.
      */}
      {!isBuilderEditing && !appMode && (
        <ErrorBoundary FallbackComponent={() => null} onError={handleBuilderError}>
          <Box sx={{ maxWidth: '1198px', mx: 'auto', px: { xs: 2, md: 3 } }}>
            <EvergreenLinks slug={slug} />
          </Box>
        </ErrorBoundary>
      )}
      <ErrorBoundary FallbackComponent={() => null} onError={handleBuilderError}>
        <OfferingUpdateHandler offeringData={offeringData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={() => null} onError={handleBuilderError}>
        <ClosedOfferingPopup offeringData={offeringData} />
      </ErrorBoundary>
    </ErrorBoundary>
  );
};

BuilderLayoutClient.propTypes = {
  offeringData: PropTypes.object.isRequired,
  initialContent: PropTypes.object,
  variantKey: PropTypes.string,
  variantSource: PropTypes.string,
  requestedVariant: PropTypes.string,
  preview: PropTypes.bool,
  previewPageType: PropTypes.string,
  perIssuerFlagKey: PropTypes.string,
};

export default BuilderLayoutClient;
